import { Meteor } from 'meteor/meteor';
import { escapeRegExp } from '/imports/utils';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';

/**
 * MTGO users. Keyed by the numeric MTGO user id; handlers coerce string → number
 * at the Mongo query boundary.
 */
export interface MUser extends Doc {
  _id: number;
  name: string;
  email: string | null;
  utyp: string | null;
  clan: string;
  /** LEGACY flat balance. No longer read or written by trading -- kept for
   *  rollback; live balances migrated into `credits` (20260807 migration). */
  credit: number;
  /** Fleet-scoped credit: fleetId (or 'none' for unfleeted bots) -> tix. */
  credits?: Record<string, number>;
  cash: number;
}

type Model = MUser;
type Refs = References<Model>;

export const MUsers = new Mongo.Collection<MUser>('musers');

export const listMUsers = DeclareMethod<ListMethodArg<MUser>, ListMethodResponse<MUser, Refs>>('listMUsers');
export const getMUser = DeclareMethod<string, GetMethodResponse<MUser, Refs>>('getMUser');
export const createMUser = DeclareMethod<Partial<MUser>, string>('createMUser');
export const updateMUser = DeclareMethod<Partial<MUser>, string>('updateMUser');
export const deleteMUser = DeclareMethod<string, undefined>('deleteMUser');


/**
 * musers is the largest collection in the database (~1.1M docs) and had no
 * index at all beyond `_id`, while the admin list runs a case-insensitive
 * regex over `name` on every keystroke -- a full collection scan each time.
 *
 * Built here as a fire-and-forget IIFE rather than inside `Meteor.startup`, and
 * that placement is the point: startup hooks are awaited before the server
 * listens, and a rejecting one calls `process.exit(1)`. An index build over
 * 1.1M documents in that position would delay or kill boot.
 *
 * An unanchored /foo/i still cannot seek on a btree, so this turns a collection
 * scan into an index scan rather than a lookup. Making it a true seek would
 * mean anchoring the pattern and adding a case-insensitive collation, which
 * changes 'contains' to 'starts with' -- a product decision, not a perf fix.
 */
Meteor.isServer && (async () => {
  try {
    await MUsers.rawCollection().createIndex({ name: 1 });
  }
  catch (e: any) {
    console.warn('[musers] name index creation failed', e?.message ?? e);
  }
})();

export const modelClass: ModelClass<Model, Refs> = {
  collection: MUsers,
  editableFields: ['name', 'email', 'utyp', 'clan', 'credits', 'cash'],
  // Lets the list method (and the muser picklist) filter by username; a numeric
  // search also matches the uid.
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    // The search term goes straight into a RegExp, so it has to be escaped:
    // typing a bare '(' in the admin search would otherwise throw SyntaxError
    // out of the list method instead of returning no matches.
    const or: Mongo.Query<MUser>[] = [{ name: new RegExp(escapeRegExp(search), 'i') } as any];
    if (/^\d+$/.test(search)) or.push({ _id: Number(search) } as any);
    return { $or: or } as any;
  },
  listMethod: listMUsers,
  getMethod: getMUser,
  createMethod: createMUser,
  updateMethod: updateMUser,
  deleteMethod: deleteMUser,
  pathPrefix: '/musers',
  modelName: 'MUser',
  pluralName: 'MUsers',
};
